import { useEffect, useRef, useState } from 'react';

const funnelSteps = [
  { label: 'Kampanya Açılır', pct: '100%', users: '2.847', color: '#00d9ff' },
  { label: 'İnceleme', pct: '68%', users: '1.936', color: '#4db8ff' },
  { label: 'İkinci Adım', pct: '42%', users: '1.196', color: '#d4af37' },
  { label: 'Teklif', pct: '24%', users: '683', color: '#f4d03f' },
  { label: 'WhatsApp / Form', pct: '13%', users: '370', color: '#d4af37' },
];

const analysisCards = [
  { icon: 'ri-timer-line', title: 'İzleme Süresi Ölçülür', desc: 'Her kampanyada geçirilen süre saniye bazında kaydedilir ve raporlanır.' },
  { icon: 'ri-map-pin-line', title: 'Hangi Kampanyada Kaldı Görülür', desc: 'Ziyaretçinin en çok ilgilendiği kampanya tespit edilir, kişiselleştirilmiş takip yapılır.' },
  { icon: 'ri-line-chart-line', title: 'Çıkış Noktası Analiz Edilir', desc: 'Kullanıcının hangi adımda ayrıldığı belirlenir, dönüşüm engelleri ortadan kaldırılır.' },
];

export default function CampaignFlow() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="kampanya-akisi" className="relative py-24 md:py-32 bg-[#0d1117] overflow-hidden">
      {/* Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[300px] bg-[#d4af37]/6 rounded-full blur-[120px] pointer-events-none" />

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#d4af37] text-xs font-bold uppercase tracking-[4px] mb-4">
            Kampanya Akışı
          </span>
          <h2 className="text-3xl md:text-5xl font-black text-white leading-tight mb-4">
            Ziyaretçi Yolculuğu{' '}
            <span style={{ background: 'linear-gradient(135deg, #00d9ff, #d4af37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Nasıl İzlenir?
            </span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            Her adım ölçülür, her tıklama kaydedilir, her çıkış analiz edilir
          </p>
        </div>

        {/* Funnel */}
        <div className="flex flex-col items-center gap-2 mb-16">
          {funnelSteps.map((step, i) => {
            const widthPct = 100 - i * 14;
            return (
              <div
                key={i}
                className={`transition-all duration-700 w-full flex justify-center ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                <div
                  className="relative rounded-xl px-6 py-4 flex items-center justify-between border border-white/10 hover:border-white/20 transition-all duration-300 group"
                  style={{
                    width: `${widthPct}%`,
                    background: `linear-gradient(135deg, rgba(26,26,46,0.9), rgba(13,17,23,0.9))`,
                    backdropFilter: 'blur(20px)',
                    borderLeft: `3px solid ${step.color}`,
                  }}
                >
                  {/* Progress bar */}
                  <div
                    className="absolute bottom-0 left-0 h-0.5 rounded-b-xl transition-all duration-1000"
                    style={{
                      width: visible ? step.pct : '0%',
                      background: `linear-gradient(90deg, ${step.color}, transparent)`,
                      transitionDelay: `${i * 120 + 400}ms`,
                    }}
                  />

                  <div className="flex items-center gap-4">
                    <div
                      className="w-8 h-8 flex items-center justify-center rounded-full text-black text-xs font-black shrink-0"
                      style={{ background: `linear-gradient(135deg, ${step.color}, ${step.color}99)` }}
                    >
                      {i + 1}
                    </div>
                    <span className="text-white font-semibold text-sm md:text-base">{step.label}</span>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className="text-white/40 text-sm hidden sm:block">{step.users} kullanıcı</span>
                    <span
                      className="text-sm font-black"
                      style={{ color: step.color }}
                    >
                      {step.pct}
                    </span>
                  </div>
                </div>

                {/* Arrow */}
                {i < funnelSteps.length - 1 && (
                  <div className="absolute mt-14 flex items-center justify-center">
                    <i className="ri-arrow-down-s-line text-white/20 text-xl"></i>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Analysis Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {analysisCards.map((card, i) => (
            <div
              key={i}
              className={`rounded-2xl p-6 border border-white/10 hover:border-[#d4af37]/30 transition-all duration-700 hover:-translate-y-1 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{
                background: 'rgba(26, 26, 46, 0.6)',
                backdropFilter: 'blur(20px)',
                transitionDelay: `${i * 100 + 600}ms`,
              }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#d4af37]/10 mb-4">
                <i className={`${card.icon} text-2xl text-[#d4af37]`}></i>
              </div>
              <h4 className="text-white font-bold text-base mb-2">{card.title}</h4>
              <p className="text-white/50 text-sm leading-relaxed">{card.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
